import React, { memo } from 'react';
import styled from 'styled-components';

import { HourlyForecastPeriodCompact } from './types';
import { smallMediaQuery } from '../../../common/utilities/sizeHelpers';
import { reduxConnect } from '../../../common';
import { HourlyForecastListItemView } from './hourlyForecastListItemView';
import { formatDateStr, defaultReading, hourlyForecastCardsListId } from './utils';
import { withScreenSize } from '../../common/utilities/withScreenSize.jsx';
import { HouryForecastViewLoadingUI } from '../hourlyForecast/loadingUI/hourlyForecastViewLoadingUI';

const dayStrFormatMobile = 'EEEE';
const dayStrFormatLarge = 'EEE';

interface HourlyForecastCardsListProps {
    hourlyForecastPeriods: HourlyForecastPeriodCompact[];
    isMobile: boolean;
    isLoadingHourlyView: boolean;
    windUnit: number;
    tempUnit: number;
}

const mapStoreStateToProps = (storeState: any) => {
    const { hourlyForecastPage, solar, forecast, units } =  storeState || {};
    const { hourlyForecastPeriods } = hourlyForecastPage || {};
    const { windUnit, tempUnit } = units || {};

    const { isLoading } = solar || {};
    const { hourlyForecastIsRefreshing } = forecast || {};
    const isLoadingHourlyView = (isLoading || hourlyForecastIsRefreshing) && !hourlyForecastPeriods?.length;

    return {
        hourlyForecastPeriods,
        isLoadingHourlyView,
        windUnit,
        tempUnit
    };
};

const mapDispatchToProps = {};

export const HourlyForecastCardsList = reduxConnect(mapStoreStateToProps, mapDispatchToProps)(memo(withScreenSize(({
    isMobile,
    isLoadingHourlyView,
    hourlyForecastPeriods = [] }: HourlyForecastCardsListProps) => {

    let refPrevCardDay = '';
    
    if (isLoadingHourlyView) {
        return <HouryForecastViewLoadingUI />;
    }
    
    return (
        <Container id={hourlyForecastCardsListId} key='hourly-forecast-cards-list'>
            {
                hourlyForecastPeriods?.map((forecastPeriod, forecastPeriodIndex) => {
                    const {
                        ic: iconCode,
                        d: description, 
                        fdtolcl: forecastDateLocalStr, 
                        t: temperature, 
                        dpt: dewPoint, 
                        flt: feelsLike, 
                        rh: relativeHumidity, 
                        wd: windDirectionDegrees,
                        pp: precipProbability,
                        ws: windSpeed  } = forecastPeriod;
                    const dayFormat = isMobile ? dayStrFormatMobile : dayStrFormatLarge;
                    const dayDisplay = forecastDateLocalStr ? formatDateStr(forecastDateLocalStr, dayFormat) : defaultReading;
                    const displayWeekday = dayDisplay?.toLowerCase() !== refPrevCardDay?.toLowerCase();
                    const convertedWindSpeed = windSpeed;
                    const feelsLikeDisplay  = Math.abs(feelsLike) >= 0 ? `${feelsLike}°` : defaultReading;
                    const dewPointDisplay = Math.abs(dewPoint) >= 0 ? `${dewPoint}°` : defaultReading;
                    const temperatureDisplay = Math.abs(temperature as number) >= 0 ? `${temperature}°` : defaultReading;
    
                    refPrevCardDay = dayDisplay as string;

                    return (
                        <HourlyForecastListItemView
                            key={`hourly-card-list-item-view#${forecastPeriodIndex}`}
                            forecastDateLocalStr={forecastDateLocalStr}
                            iconCode={iconCode}
                            description={description}
                            temperature={temperatureDisplay}
                            dewPoint={dewPointDisplay}
                            feelsLike={feelsLikeDisplay}
                            windDirectionDegrees={windDirectionDegrees}
                            precipitation={precipProbability}
                            humidity={relativeHumidity}
                            windSpeed={convertedWindSpeed}
                            isMobile={isMobile}
                            dayDisplay={dayDisplay as string}
                            displayWeekday={displayWeekday}
                            forecastPeriodIndex={forecastPeriodIndex} />
                    );
                })
            }
        </Container>
    );
})));

const Container = styled.ul`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    width: auto;
    list-style-type: none;
    padding: 0;
    height: auto;
    @media ${smallMediaQuery} {
        width: 100%;
        height: auto;
        flex-direction: column;
    }
`;